function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import Log from '@deephaven/log';
import MaskedInput from "./MaskedInput.js";
import { getNextSegmentValue } from "./DateInputUtils.js";
import { addSeparators } from "./DateTimeInputUtils.js";
import { jsx as _jsx } from "react/jsx-runtime";
var log = Log.module('DateTimeInput');

// This could be more restrictive and restrict days to the number of days in the month...
// But then gotta take leap year into account and everything.
var DATE_PATTERN = '[12][0-9]{3}-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])';
// Put zero width spaces in the nanosecond part of the date to allow jumping between segments
var TIME_PATTERN = '([01][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]\\.[0-9]{3}\u200B[0-9]{3}\u200B[0-9]{3}';
var FULL_DATE_PATTERN = "".concat(DATE_PATTERN, " ").concat(TIME_PATTERN);
var DATE_VALUE_STRING = '2022-01-01';
var DEFAULT_VALUE_STRING = "".concat(DATE_VALUE_STRING, " 00:00:00.000000000");
var FULL_DATE_FORMAT = 'YYYY-MM-DD HH:MM:SS.SSSSSSSSS';
function fixIncompleteValue(value) {
  if (value != null && value.length >= DATE_VALUE_STRING.length) {
    return "".concat(value.substring(0, DATE_VALUE_STRING.length)).concat(value.substring(DATE_VALUE_STRING.length).replace(/\u2007/g, '0')).concat(DEFAULT_VALUE_STRING.substring(value.length));
  }
  return value;
}
function removeSeparators(value) {
  return value.replace(/\u200B/g, '');
}
var EXAMPLES = [addSeparators(DEFAULT_VALUE_STRING)];
export var DateTimeInput = /*#__PURE__*/React.forwardRef((props, ref) => {
  var _props$className = props.className,
    className = _props$className === void 0 ? '' : _props$className,
    _props$onChange = props.onChange,
    onChange = _props$onChange === void 0 ? () => undefined : _props$onChange,
    _props$defaultValue = props.defaultValue,
    defaultValue = _props$defaultValue === void 0 ? '' : _props$defaultValue,
    _props$onFocus = props.onFocus,
    onFocus = _props$onFocus === void 0 ? () => undefined : _props$onFocus,
    _props$onBlur = props.onBlur,
    onBlur = _props$onBlur === void 0 ? () => undefined : _props$onBlur,
    onSubmit = props.onSubmit,
    dataTestId = props['data-testid'];
  var _useState = useState(defaultValue.length > 0 ? addSeparators(defaultValue) : ''),
    _useState2 = _slicedToArray(_useState, 2),
    value = _useState2[0],
    setValue = _useState2[1];
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    selection = _useState4[0],
    setSelection = _useState4[1];

  /**
   * Normalize text by:
   * - Replacing 'T' with space to support ISO 8601 format
   * - Removing timezone information (e.g., "EDT", "+05:00", "Z")
   * - Adding zero-width space separators in the nanosecond part
   * @param text The text
   * @returns The normalized text
   */
  var normalizeText = useCallback(text => {
    // Replace first 'T' separator with space for ISO 8601 format (without global flag to preserve 'T' in timezone like EDT)
    var normalized = text.replace(/T/, ' ');

    // Remove timezone information
    // Match datetime up to optional fractional seconds, then strip everything else
    // Pattern: YYYY-MM-DD HH:MM:SS[.SSSSSSSSS] followed by optional timezone
    var dateTimeMatch = normalized.match(/^(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}(?:\.\d+)?)/);
    if (dateTimeMatch) {
      var _dateTimeMatch = _slicedToArray(dateTimeMatch, 2);
      normalized = _dateTimeMatch[1];
    }

    // Add zero-width space separators to match the expected pattern
    return addSeparators(normalized);
  }, []);

  // Sync internal state with defaultValue prop when it changes
  // Apply normalization to handle raw unformatted values (e.g., with timezone info)
  useEffect(() => {
    if (defaultValue.length > 0) {
      var normalized = normalizeText(defaultValue);
      setValue(normalized);
      // Notify parent with the normalized value (without separators)
      onChange(fixIncompleteValue(removeSeparators(normalized)));
    } else {
      setValue('');
      onChange('');
    }
  }, [defaultValue, normalizeText, onChange]);
  var handleChange = useCallback(newValue => {
    log.debug('handleChange', newValue);
    setValue(newValue);
    onChange(fixIncompleteValue(removeSeparators(newValue)));
  }, [onChange]);
  var handleBlur = useCallback(() => {
    var prevValue = removeSeparators(value);
    var fixedValue = fixIncompleteValue(prevValue);
    // Update the value displayed in the input
    // onChange with the fixed value already triggered in handleChange
    if (fixedValue !== prevValue) {
      setValue(addSeparators(fixedValue));
    }
    onBlur();
  }, [value, onBlur]);
  return /*#__PURE__*/_jsx("div", {
    className: "d-flex flex-row align-items-center",
    children: /*#__PURE__*/_jsx(MaskedInput, {
      ref: ref,
      className: classNames(className),
      example: EXAMPLES,
      getNextSegmentValue: getNextSegmentValue,
      normalizePastedText: normalizeText,
      onChange: handleChange,
      onSelect: setSelection,
      onSubmit: onSubmit,
      pattern: FULL_DATE_PATTERN,
      placeholder: FULL_DATE_FORMAT,
      selection: selection,
      value: value,
      onFocus: onFocus,
      onBlur: handleBlur,
      "data-testid": dataTestId
    })
  });
});
DateTimeInput.displayName = 'DateTimeInput';
export default DateTimeInput;
//# sourceMappingURL=DateTimeInput.js.map